import { newCorrelationId } from "./correlation";
import { getAccessToken, isAccessExpired, refreshAccessToken, clearTokens } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly correlationId: string,
    public readonly body: unknown,
    message: string,
  ) { super(message); this.name = "ApiError"; }
}

async function attachAuth(headers: Headers): Promise<void> {
  if (isAccessExpired()) await refreshAccessToken();
  const tok = getAccessToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
}

export async function apiFetch<T = unknown>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const correlationId = newCorrelationId();
  const headers = new Headers(init.headers);
  headers.set("X-Correlation-Id", correlationId);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  await attachAuth(headers);

  let res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
      res = await fetch(input, { ...init, headers });
    } else {
      clearTokens();
    }
  }
  const body: unknown = await (res.json() as Promise<unknown>).catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, correlationId, body,
      `API ${res.status} ${typeof body === "object" && body && "title" in body ? (body as { title: string }).title : ""}`.trim());
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Convenience wrappers used by route-level modules.
// ---------------------------------------------------------------------------

/** Authenticated GET — returns parsed JSON. */
export const get = <T>(url: string): Promise<T> =>
  apiFetch<T>(url);

/** Authenticated POST — returns parsed JSON. */
export const post = <T>(url: string, body: unknown): Promise<T> =>
  apiFetch<T>(url, { method: "POST", body: JSON.stringify(body) });

/**
 * Authenticated request that returns the raw response body as a Blob. Mirrors
 * apiFetch's auth + correlation + 401-refresh handling. Used for binary endpoints
 * (PDFs) that need to be embedded in &lt;iframe&gt; or anchor downloads — those native
 * elements can't attach the Authorization header on their own — and for POSTs that
 * return a PDF, such as the parcel-label print call (pass `init` with method/body).
 *
 * On a non-2xx response it parses the ProblemDetails body and throws an ApiError
 * carrying its `title`, so callers can surface the server's message rather than a
 * bare status code.
 */
export async function apiFetchBlob(input: RequestInfo, init: RequestInit = {}): Promise<Blob> {
  return (await apiFetchRaw(input, init)).blob();
}

/**
 * Authenticated request that returns the raw response body as text. Same auth +
 * correlation + 401-refresh + ProblemDetails-error handling as apiFetchBlob; differs
 * only in decoding. Used for endpoints that return a non-JSON text payload — the
 * parcel-label print call with `?format=zpl`, whose body is ZPL a workstation agent
 * hands straight to the printer.
 */
export async function apiFetchText(input: RequestInfo, init: RequestInit = {}): Promise<string> {
  return (await apiFetchRaw(input, init)).text();
}

/**
 * Shared body of apiFetchBlob/apiFetchText: performs the authenticated request and
 * throws on a non-2xx, leaving the caller to decode the successful body. Kept private
 * so the decoding wrappers stay the public surface.
 */
async function apiFetchRaw(input: RequestInfo, init: RequestInit): Promise<Response> {
  const correlationId = newCorrelationId();
  const headers = new Headers(init.headers);
  headers.set("X-Correlation-Id", correlationId);
  await attachAuth(headers);

  let res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
      res = await fetch(input, { ...init, headers });
    } else {
      clearTokens();
    }
  }
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    throw new ApiError(res.status, correlationId, body,
      `API ${res.status} ${typeof body === "object" && body && "title" in body ? (body as { title: string }).title : ""}`.trim());
  }
  return res;
}

// ---------------------------------------------------------------------------
// Raw (unauthenticated) helpers — for public / token-based endpoints that
// must NOT send a bearer token.
// ---------------------------------------------------------------------------

export async function getRaw<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new ApiError(r.status, "", null, `API ${r.status}`);
  return r.json() as Promise<T>;
}

export async function postRaw<T = void>(url: string, body: unknown): Promise<T | void> {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new ApiError(r.status, "", null, `API ${r.status}`);
  if (r.status === 204) return;
  return r.json() as Promise<T>;
}
